import React, { forwardRef, ReactNode } from 'react';
import styled from 'styled-components';
import { Box } from '../Box/Box';
import { AffixIconsProps } from '../common/AffixIcons';
import Stack from '../Stack/Stack';
import * as CSS from 'csstype';
import { ColorKeys, palette } from '../../theme/colors';
import { SolvTheme } from '../../theme/theme';

const StyledBase = styled(Stack)<{
  $error?: boolean | string;
  $warning?: boolean | string;
  $disabled?: Boolean | string;
}>`
  &:focus-within {
    box-shadow: 0 0 0 1px black;
    border-color: ${palette.gray[600]};
  }

  &:hover:not(:has(:disabled)) {
    border-color: ${(props) =>
      !!props.$error
        ? palette.cherry.dark
        : !!props.$warning
        ? palette.peach.dark
        : palette.gray[600]};
  }
`;

export type InputBaseProps = AffixIconsProps & {
  /** Whether to show a smaller input */
  dense?: boolean;

  /** The content of the input base */
  children: ReactNode;

  /** What cursor to render */
  cursor?: CSS.Property.Cursor;

  /** Whether the input is disabled */
  disabled?: boolean;

  /** If there is an error */
  error?: string | boolean;

  /** If there is an warning */
  warning?: string | boolean;

  /** Disable padding */
  noPadding?: boolean;

  /** Whether the height should be variable */
  variableHeight?: boolean;
};

const InputBase = forwardRef(
  (
    {
      children,
      dense,
      startIcon,
      endIcon,
      disabled,
      cursor,
      error,
      warning,
      noPadding,
      variableHeight,
    }: InputBaseProps,
    ref,
  ) => {
    let borderColor = 'gray-400' as ColorKeys;
    if (disabled) {
      borderColor = 'gray-300';
    } else if (error) {
      borderColor = 'cherry';
    } else if (warning) {
      borderColor = 'peach-dark';
    }
    return (
      <StyledBase
        position="relative"
        border="solid 1px"
        borderColor={borderColor}
        borderRadius="base"
        className="focusable"
        color="gray-600"
        textAlign="left"
        px={noPadding ? 0 : 2}
        height={variableHeight ? undefined : SolvTheme.space[dense ? 6 : 7]}
        alignItems="center"
        $error={error}
        $warning={warning}
        $disabled={disabled}
        sx={{
          cursor: disabled ? 'default' : cursor,
          '--focus-offset': 0,
          '--focus-width': 2,
        }}
        ref={ref}
        row
      >
        {startIcon != null && startIcon}
        <Box
          flex="1"
          justifyContent="stretch"
          display="flex"
          alignItems="stretch"
          alignSelf="stretch"
        >
          {children}
        </Box>
        {endIcon != null && endIcon}
      </StyledBase>
    );
  },
);

export default InputBase;
